/**
 * Role-based capability model for ShopLedger.
 *
 * Why this exists
 * ---------------
 * Before this module, every UI gate sprinkled across the app looked
 * like `if (currentRole === "owner" || currentRole === "manager")` or
 * `if (isApprentice && canTattoo)`. That coupled UI to roles directly
 * and made it impossible to answer "what does an artist actually see?"
 * without grepping every component. It also meant that adding a new
 * role or shifting one permission between roles touched dozens of
 * files.
 *
 * This module centralizes the decision. Every gated feature gets one
 * `Capability`. Every role gets a default capability set. The
 * `computeCapabilities()` function applies context-specific overlays
 * (Solo Free strips Pro-only caps; Front Desk mode replaces the cap
 * set entirely; apprentice + canTattoo adds tattoo-flow caps; etc.)
 * and returns the final set. UI then asks `can("view_finance")` and
 * never deals with role strings directly.
 *
 * Design decisions locked 2026-05-15 in
 * `Shop Ledger/phase-1-role-based-ui-hiding.md` §11:
 *   - Manager does NOT see Subscription or Stripe Connect (owner only)
 *   - Apprentices KEEP Consultations visible
 *   - Piercers DO NOT see Waitlist
 *   - Artists DO NOT see Events (owner/manager only)
 */
import type { Database } from "@/integrations/supabase/types";

export type ShopRole = Database["public"]["Enums"]["shop_role"];

/**
 * All atomic capabilities in the system. Adding a new gated feature?
 * Add the capability here, decide which roles get it by default in
 * ROLE_DEFAULTS below, and gate the UI with `can(...)`.
 */
export type Capability =
  // Navigation / page-level
  | "view_dashboard"
  | "view_appointments"
  | "view_clients"
  | "view_consultations"
  | "view_messages"
  | "view_shop_chat"
  | "view_finance"
  | "view_pos"
  | "view_gift_cards"
  | "view_team"
  | "view_artists"
  | "view_piercers"
  | "view_apprenticeships"
  | "view_inventory"
  | "view_events"
  | "view_visiting_artists"
  | "view_consent_forms"
  | "view_email_campaigns"
  | "view_waitlist"
  | "view_audit_logs"
  | "view_settings"
  // Settings cards
  | "manage_subscription"
  | "manage_stripe_connect"
  | "manage_shop_info"
  | "manage_booking_widget"
  | "manage_funding_account"
  | "manage_pay_periods"
  | "manage_sms"
  | "manage_bank_connections"
  | "manage_consultation_embed"
  | "edit_public_profile"
  | "edit_portfolio"
  | "manage_own_payout"
  | "host_guest_artists"
  // Actions
  | "send_payouts"
  | "close_pay_period"
  | "manage_team_members"
  | "view_all_pay"
  | "view_own_pay"
  | "process_pos_sale"
  | "refund_transaction"
  | "manage_appointments_all"
  | "manage_appointments_own"
  // Admin
  | "platform_admin";

/**
 * Every capability in the system. Kept here so platform admins and
 * owners can be granted "all" without enumerating each one inline.
 * Update this list when adding to `Capability` above — TS will not
 * catch a missing entry here at compile time, but the unit-style
 * check at the bottom of this file will catch it at module load.
 */
const ALL_CAPABILITIES: readonly Capability[] = [
  "view_dashboard",
  "view_appointments",
  "view_clients",
  "view_consultations",
  "view_messages",
  "view_shop_chat",
  "view_finance",
  "view_pos",
  "view_gift_cards",
  "view_team",
  "view_artists",
  "view_piercers",
  "view_apprenticeships",
  "view_inventory",
  "view_events",
  "view_visiting_artists",
  "view_consent_forms",
  "view_email_campaigns",
  "view_waitlist",
  "view_audit_logs",
  "view_settings",
  "manage_subscription",
  "manage_stripe_connect",
  "manage_shop_info",
  "manage_booking_widget",
  "manage_funding_account",
  "manage_pay_periods",
  "manage_sms",
  "manage_bank_connections",
  "manage_consultation_embed",
  "edit_public_profile",
  "edit_portfolio",
  "manage_own_payout",
  "host_guest_artists",
  "send_payouts",
  "close_pay_period",
  "manage_team_members",
  "view_all_pay",
  "view_own_pay",
  "process_pos_sale",
  "refund_transaction",
  "manage_appointments_all",
  "manage_appointments_own",
  "platform_admin",
] as const;

/**
 * Default capability set per role. Context overlays in
 * `computeCapabilities()` can add or remove from these.
 *
 * Matrix reflects the visibility decisions in §11 of the design doc
 * (resolved 2026-05-15).
 */
const ROLE_DEFAULTS: Record<ShopRole, readonly Capability[]> = {
  // Owner gets everything except `platform_admin` — that's a separate
  // global-admin escape hatch granted via the `platform_admins` table.
  owner: ALL_CAPABILITIES.filter((c) => c !== "platform_admin"),

  manager: [
    // Same nav as owner...
    "view_dashboard",
    "view_appointments",
    "view_clients",
    "view_consultations",
    "view_messages",
    "view_shop_chat",
    "view_finance",
    "view_pos",
    "view_gift_cards",
    "view_team",
    "view_artists",
    "view_piercers",
    "view_apprenticeships",
    "view_inventory",
    "view_events",
    "view_visiting_artists",
    "view_consent_forms",
    "view_email_campaigns",
    "view_waitlist",
    "view_audit_logs",
    "view_settings",
    // ...same settings cards EXCEPT billing-adjacent (owner-only per §11 Q1+Q2)
    "manage_shop_info",
    "manage_booking_widget",
    "manage_funding_account",
    "manage_pay_periods",
    "manage_sms",
    "manage_bank_connections",
    "manage_consultation_embed",
    "edit_public_profile",
    "edit_portfolio",
    "manage_own_payout",
    // Actions
    "send_payouts",
    "close_pay_period",
    "manage_team_members",
    "view_all_pay",
    "process_pos_sale",
    "refund_transaction",
    "manage_appointments_all",
    // Excluded by design:
    //   "manage_subscription"     — owner only (§11 Q1)
    //   "manage_stripe_connect"   — owner only (§11 Q2)
    //   "view_own_pay"            — added by is_also_artist overlay below
  ],

  artist: [
    "view_dashboard",
    "view_appointments",
    "view_clients",
    "view_consultations",
    "view_messages",
    "view_shop_chat",
    "view_pos",
    "view_consent_forms",
    "view_settings",
    "edit_public_profile",
    "edit_portfolio",
    "manage_own_payout",
    "view_own_pay",
    "process_pos_sale",
    "manage_appointments_own",
    // Excluded by design:
    //   "view_events"    — owner/manager only (§11 Q5)
    //   "view_waitlist"  — shop-level admin only (existing decision)
  ],

  apprentice: [
    "view_dashboard",
    "view_consultations",
    "view_messages",
    "view_shop_chat",
    "view_apprenticeships",
    "view_settings",
    "edit_public_profile",
    // canTattoo overlay below adds the tattoo-flow caps for the
    // apprentices who are actually doing client work.
  ],

  piercer: [
    "view_dashboard",
    "view_appointments",
    "view_clients",
    "view_consultations",
    "view_messages",
    "view_shop_chat",
    "view_pos",
    "view_consent_forms",
    "view_settings",
    "edit_public_profile",
    "edit_portfolio",
    "manage_own_payout",
    "view_own_pay",
    "process_pos_sale",
    "manage_appointments_own",
    // Excluded by design:
    //   "view_waitlist"  — piercers don't use tattoo-style waitlists (§11 Q4)
  ],
};

/**
 * Capabilities granted to a Solo Booker — the $9/mo booking-only
 * subscription. This is the tightest paying-customer cap set in the
 * system: just the surfaces needed to take bookings (calendar,
 * booking widget, clients, consults, consent forms, settings). No
 * Finance, POS, Events, Email Campaigns, SMS, Stripe Connect, Bank
 * Connections, etc. Designed for solo artists who churned to Acuity
 * / Calendly because Solo Pro felt like too much app.
 *
 * Decision doc: `Shop Ledger/phase-2-solo-booker-tier.md`.
 */
const SOLO_BOOKER_DEFAULTS: readonly Capability[] = [
  // Navigation
  "view_dashboard",
  "view_appointments",
  "view_clients",
  "view_consultations",
  "view_consent_forms",
  "view_settings",
  // Settings cards (their own single-seat shop)
  "manage_shop_info",
  "manage_booking_widget",
  "manage_consultation_embed",
  "manage_subscription",
  "manage_own_payout",
  "edit_public_profile",
  "edit_portfolio",
  // Actions
  "view_own_pay",
  "manage_appointments_own",
];

/**
 * Capabilities granted to a Solo Artist (a user with zero shop
 * memberships, who runs a single-seat personal practice). Solo
 * Artists have no ShopRole because they have no shop_memberships
 * row — they're modeled as a virtual pseudo-role here.
 *
 * The list is roughly "owner of your own personal solo shop, minus
 * any team / multi-artist surfaces." Solo Free further strips the
 * Pro-only caps via SOLO_FREE_LOCKED below.
 *
 * Explicitly excluded (intentionally):
 *   view_team, view_artists, view_piercers, view_apprenticeships,
 *   view_visiting_artists, view_audit_logs, view_shop_chat,
 *   view_gift_cards, view_inventory, manage_team_members,
 *   close_pay_period, manage_pay_periods, send_payouts, view_all_pay,
 *   manage_funding_account, manage_appointments_all, platform_admin.
 */
const SOLO_ARTIST_DEFAULTS: readonly Capability[] = [
  // Navigation
  "view_dashboard",
  "view_appointments",
  "view_clients",
  "view_consultations",
  "view_messages",
  "view_finance",
  "view_pos",
  "view_events",
  "view_email_campaigns",
  "view_consent_forms",
  "view_waitlist",
  "view_settings",
  // Settings cards (their own single-seat shop)
  "manage_subscription",
  "manage_stripe_connect",
  "manage_shop_info",
  "manage_booking_widget",
  "manage_sms",
  "manage_bank_connections",
  "manage_consultation_embed",
  "edit_public_profile",
  "edit_portfolio",
  "manage_own_payout",
  // Actions
  "view_own_pay",
  "process_pos_sale",
  "refund_transaction",
  "manage_appointments_own",
];

/**
 * Capabilities granted to a Solo Pro Plus subscriber — the $29/mo
 * "guest chair" tier. Solo Pro's full cap set PLUS `host_guest_artists`,
 * which unlocks the ability to add up to 2 read-only guest artist
 * profiles + a "Featured Guest" callout on the booking widget.
 *
 * NOT a multi-artist studio — no payroll, no W-9 collection, no
 * audit logs, no team management. Differentiated from Booksy/Vagaro
 * per-seat pricing by being framed as a tattoo-culture native
 * "residency / guest chair" concept rather than generic staff.
 *
 * Added based on the May 2026 deep-research recommendation. Stripe
 * products created separately; this branch is dormant in production
 * until isSoloProPlus flips true (which requires the Stripe product
 * to exist and a user to subscribe to it).
 */
const SOLO_PRO_PLUS_DEFAULTS: readonly Capability[] = [
  ...SOLO_ARTIST_DEFAULTS,
  // host_guest_artists is the one and only differentiator from Solo
  // Pro. Everything else is identical.
  "host_guest_artists",
];

/**
 * Capabilities that disappear when a Solo Artist is on the Free tier.
 * Upgrading to Solo Pro flips `isSoloFree` to false and the caps set
 * recomputes automatically.
 */
const SOLO_FREE_LOCKED: readonly Capability[] = [
  "view_finance",
  "view_pos",
  "view_events",
  "view_email_campaigns",
  "view_gift_cards",
  "view_inventory",
  "manage_pay_periods",
  "manage_funding_account",
  "send_payouts",
];

/**
 * Front Desk mode is a kiosk-like restricted session that any role
 * can activate. Its cap set REPLACES the role's default entirely —
 * the user signed in is no longer effectively their own role until
 * front-desk is deactivated with the PIN. This lets a studio owner
 * hand the iPad to a receptionist without exposing Finance, Team,
 * Settings, etc.
 */
const FRONT_DESK_ALLOWED: readonly Capability[] = [
  "view_appointments",
  "view_clients",
  "view_consultations",
  "view_pos",
  "process_pos_sale",
];

export interface CapabilityContext {
  /** Current role from the active shop membership, or null if signed-out / no shop. */
  role: ShopRole | null;
  /** True when the user has zero shop memberships (Solo Artist tier family). */
  isSoloArtist: boolean;
  /** Solo Artist on the Free tier — strips Pro-only caps. */
  isSoloFree: boolean;
  /**
   * Solo Booker tier ($9/mo) — the booking-only sub. Receives a much
   * tighter cap set than Solo Pro / Solo Free; intended for solo
   * artists who want just calendar + booking widget + clients and
   * would otherwise churn to Acuity / Calendly. When true, this
   * overrides the Solo Artist Pro/Free branch entirely.
   */
  isSoloBooker: boolean;
  /**
   * Solo Pro Plus tier ($29/mo) — Solo Pro with the "guest chair"
   * addition. Identical to Solo Artist's full Pro cap set with
   * `host_guest_artists` added so the user can invite up to 2
   * read-only guest/visiting artist profiles. When true, this takes
   * precedence over the regular Solo Artist branch.
   */
  isSoloProPlus: boolean;
  /** True when `role === "apprentice"`. Required for the canTattoo overlay to fire. */
  isApprentice: boolean;
  /** `apprenticeship_profiles.can_tattoo` for the current shop. */
  apprenticeCanTattoo: boolean;
  /** Front Desk kiosk mode active in this session. */
  isFrontDeskMode: boolean;
  /** Member of `platform_admins` — sees and can do everything across all shops. */
  isPlatformAdmin: boolean;
  /** `shop_memberships.is_also_artist` — manager who also tattoos, gets `view_own_pay`. */
  isAlsoArtist: boolean;
}

/**
 * Compute the user's effective capability set from their role +
 * context overlays. Pure function; safe to memoize in hooks.
 *
 * Precedence (high to low):
 *   1. Platform admin           -> ALL_CAPABILITIES (ignores everything else).
 *   2. Front Desk mode          -> FRONT_DESK_ALLOWED only (ignores role).
 *   3. Solo Booker              -> SOLO_BOOKER_DEFAULTS (booking-only $9 tier).
 *      Takes precedence over the general Solo Artist branch since
 *      Booker is a paid sub with its own (smaller) cap set.
 *   4. Solo Pro Plus            -> SOLO_PRO_PLUS_DEFAULTS ($29 "guest chair").
 *      Same as Solo Artist Pro plus host_guest_artists.
 *   5. Solo Artist              -> SOLO_ARTIST_DEFAULTS (minus SoloFree locks).
 *      Solo Artists have no shop_memberships row and thus no role; they
 *      use a curated single-seat cap set so UI gates work for them
 *      without special-casing in every component.
 *   6. Role default + overlays  -> canTattoo, is_also_artist, SoloFree.
 *   7. No role and not a Solo Artist -> empty set (signed-in but in a
 *      transient state; UI should bounce them elsewhere).
 */
export function computeCapabilities(ctx: CapabilityContext): Set<Capability> {
  if (ctx.isPlatformAdmin) {
    return new Set<Capability>(ALL_CAPABILITIES);
  }

  if (ctx.isFrontDeskMode) {
    return new Set<Capability>(FRONT_DESK_ALLOWED);
  }

  if (ctx.isSoloBooker) {
    return new Set<Capability>(SOLO_BOOKER_DEFAULTS);
  }

  if (ctx.isSoloProPlus) {
    return new Set<Capability>(SOLO_PRO_PLUS_DEFAULTS);
  }

  if (ctx.isSoloArtist) {
    const caps = new Set<Capability>(SOLO_ARTIST_DEFAULTS);
    if (ctx.isSoloFree) {
      for (const locked of SOLO_FREE_LOCKED) {
        caps.delete(locked);
      }
    }
    return caps;
  }

  if (!ctx.role) {
    return new Set<Capability>();
  }

  const caps = new Set<Capability>(ROLE_DEFAULTS[ctx.role]);

  // Apprentice + canTattoo: gains the tattoo-flow caps a regular
  // artist has. Without canTattoo, apprentices are studio-floor-only
  // and don't touch the appointment / client UI.
  if (ctx.isApprentice && ctx.apprenticeCanTattoo) {
    caps.add("view_appointments");
    caps.add("view_clients");
    caps.add("view_consent_forms");
    caps.add("view_pos");
    caps.add("manage_appointments_own");
    caps.add("process_pos_sale");
    caps.add("edit_portfolio");
    caps.add("manage_own_payout");
  }

  // Manager who is also an artist (e.g. Kyrah at LTT): keeps all
  // manager admin powers AND gains the personal-pay-card visibility a
  // regular artist would have on their dashboard.
  if (ctx.role === "manager" && ctx.isAlsoArtist) {
    caps.add("view_own_pay");
  }

  // Solo Free strips Pro-only caps. Solo Artists in this codebase
  // come through with role = "owner" of their personal single-seat
  // shop, so the SoloFree subtraction is what gates them, not their
  // role. The `useSoloArtistLimits` hook in the upstream wrapper is
  // the source of `isSoloFree`.
  if (ctx.isSoloFree) {
    for (const locked of SOLO_FREE_LOCKED) {
      caps.delete(locked);
    }
  }

  return caps;
}

// ---------------------------------------------------------------------------
// Module-load-time sanity check.
// If a new Capability is added to the type union but forgotten in
// ALL_CAPABILITIES, owner's caps will silently miss it. This catches
// the omission the next time anything imports this module in dev/test.
// (Production bundlers tree-shake the check; runtime cost is one Set
// construction per import.)
// ---------------------------------------------------------------------------
if (import.meta.env.DEV) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _exhaustiveness: Record<Capability, true> = {
    view_dashboard: true,
    view_appointments: true,
    view_clients: true,
    view_consultations: true,
    view_messages: true,
    view_shop_chat: true,
    view_finance: true,
    view_pos: true,
    view_gift_cards: true,
    view_team: true,
    view_artists: true,
    view_piercers: true,
    view_apprenticeships: true,
    view_inventory: true,
    view_events: true,
    view_visiting_artists: true,
    view_consent_forms: true,
    view_email_campaigns: true,
    view_waitlist: true,
    view_audit_logs: true,
    view_settings: true,
    manage_subscription: true,
    manage_stripe_connect: true,
    manage_shop_info: true,
    manage_booking_widget: true,
    manage_funding_account: true,
    manage_pay_periods: true,
    manage_sms: true,
    manage_bank_connections: true,
    manage_consultation_embed: true,
    edit_public_profile: true,
    edit_portfolio: true,
    manage_own_payout: true,
    host_guest_artists: true,
    send_payouts: true,
    close_pay_period: true,
    manage_team_members: true,
    view_all_pay: true,
    view_own_pay: true,
    process_pos_sale: true,
    refund_transaction: true,
    manage_appointments_all: true,
    manage_appointments_own: true,
    platform_admin: true,
  };
}
